import type { DietSupplementsState } from "./DietSupplementsContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;

export function transformDietSupplementsStateToRequest(
  state: DietSupplementsState,
): PatchPayload {
  const lifestyle: any = {};

  if (state.lifestyleId) {
    lifestyle.id = state.lifestyleId;
  }

  // ---- Diets ----
  const dietsPayload: any[] = [];
  const dietIdsToDeleteSet = new Set(state.dietIdsToDelete);

  // Add deletions
  state.dietIdsToDelete.forEach((id) => {
    if (id) dietsPayload.push({ id, op: "delete" });
  });

  if (state.hasDiets === false) {
    // Delete all existing diets
    state.diets.forEach((d) => {
      if (d.id && !dietIdsToDeleteSet.has(d.id)) {
        dietsPayload.push({ id: d.id, op: "delete" });
      }
    });
    lifestyle.hasDiets = false;
  } else {
    if (state.hasDiets !== null) {
      lifestyle.hasDiets = state.hasDiets;
    }
    // Add diets to keep
    state.diets
      .filter((d) => !d.id || !dietIdsToDeleteSet.has(d.id))
      .forEach((d) => {
        const entry: any = { diet: d.diet };
        if (d.id) entry.id = d.id;
        if (d.other != null) entry.other = d.other;
        dietsPayload.push(entry);
      });
  }

  if (dietsPayload.length > 0) {
    lifestyle.diets = dietsPayload;
  }

  // ---- Supplements ----
  const supplementsPayload: any[] = [];
  const suppIdsToDeleteSet = new Set(state.supplementIdsToDelete);

  // Add deletions
  state.supplementIdsToDelete.forEach((id) => {
    if (id) supplementsPayload.push({ id, op: "delete" });
  });

  if (state.hasDietSupplements === false) {
    // Delete all existing supplements
    state.supplements.forEach((s) => {
      if (s.id && !suppIdsToDeleteSet.has(s.id)) {
        supplementsPayload.push({ id: s.id, op: "delete" });
      }
    });
    lifestyle.hasDietSupplements = false;
  } else {
    if (state.hasDietSupplements !== null) {
      lifestyle.hasDietSupplements = state.hasDietSupplements;
    }
    // Add supplements to keep
    state.supplements
      .filter((s) => !s.id || !suppIdsToDeleteSet.has(s.id))
      .forEach((s) => {
        const entry: any = { supplement: s.supplement };
        if (s.id) entry.id = s.id;
        if (s.other != null) entry.other = s.other;
        if (s.startDate != null) entry.startDate = s.startDate;
        if (s.frequency != null) entry.frequency = s.frequency;
        if (s.notes != null) entry.notes = s.notes;
        supplementsPayload.push(entry);
      });
  }

  if (supplementsPayload.length > 0) {
    lifestyle.dietSupplements = supplementsPayload;
  }

  if (Object.keys(lifestyle).length === 0) return {};
  return { lifestyle };
}
